'use client';

import { AnimatePresence } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useState } from 'react';

import type { MetricDetails } from '../lib/healthDetailsData';
import type { HealthAndWeatherReport } from '../lib/wether/health.service';
import MetricRow from './MetricRow';

const AnimatedModal = dynamic(() => import('./AnimatedModal').then((m) => m.AnimatedModal), {
  ssr: false,
});

const HealthDetailsContent = dynamic(() => import('./HealthDetailsContent'), { ssr: false });

interface HealthWeatherProps {
  report: HealthAndWeatherReport | null;
  className?: string;
}

export default function HealthWeather({ report, className = '' }: HealthWeatherProps) {
  const [selectedMetric, setSelectedMetric] = useState<MetricDetails | null>(null);

  // чтобы не было лишних вычислений при каждом клике
  const today = useMemo(() => {
    return new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  }, []);

  const handleMetricClick = useCallback(async (id: string) => {
    // 🚀 грузим карту деталей только когда реально нужна
    const mod = await import('../lib/healthDetailsData');
    const details = mod.healthDetailsMap.get(id);
    if (details) setSelectedMetric(details);
  }, []);

  const handleCloseModal = useCallback(() => setSelectedMetric(null), []);

  if (!report) {
    return (
      <div
        className={`p-4 bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 rounded-lg ${className}`}
      >
        Не удалось загрузить данные о влиянии погоды. Попробуйте позже.
      </div>
    );
  }

  return (
    <>
      <section
        aria-labelledby="health-weather-title"
        className={`p-4 md:p-6 bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg flex flex-col ${className}`}
      >
        <h2 id="health-weather-title" className="text-2xl font-bold text-main">
          Сегодня, {today}
        </h2>

        <div className="space-y-4 mt-4 flex-grow">
          <MetricRow
            label="Индекс метеочувствительности"
            index={report.overallIndex}
            metricId="overall"
            onClick={handleMetricClick}
          />
          <MetricRow
            label="Влияние давления"
            index={report.pressureIndex}
            metricId="pressure"
            onClick={handleMetricClick}
          />

          {report.airQualityIndex && (
            <MetricRow
              label="Качество воздуха"
              index={report.airQualityIndex}
              metricId="air_quality"
              onClick={handleMetricClick}
            />
          )}

          <MetricRow
            label="Геомагнитная обстановка"
            index={report.geomagneticIndex}
            metricId="geomagnetic"
            onClick={handleMetricClick}
          />
          <MetricRow
            label="Влияние влажности"
            index={report.humidityIndex}
            metricId="humidity"
            onClick={handleMetricClick}
          />

          {report.coldStressIndex && (
            <MetricRow
              label="Индекс мороза"
              index={report.coldStressIndex}
              metricId="cold_stress"
              onClick={handleMetricClick}
            />
          )}

          {report.pollenIndex && (
            <MetricRow
              label="Уровень пыльцы"
              index={report.pollenIndex}
              metricId="pollen"
              onClick={handleMetricClick}
            />
          )}

          <MetricRow
            label="Влияние УФ-излучения"
            index={report.uvIndex}
            metricId="uv"
            onClick={handleMetricClick}
          />
        </div>
      </section>

      <AnimatePresence>
        {selectedMetric && (
          <AnimatedModal onClose={handleCloseModal}>
            <HealthDetailsContent details={selectedMetric} />
          </AnimatedModal>
        )}
      </AnimatePresence>
    </>
  );
}
